import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";

// TypeScript declaration for custom element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'dello-ai-widget': {
        'agent-id': string;
        width?: number;
        height?: number;
        allowLanguageSelection?: boolean;
        'header-text'?: string;
      };
    }
  }
}

const DELLO_WIDGET_SRC = "https://app.dello.ai/widget/dello-ai-widget.js";
const AGENT_ID = "67f88d38-db96-41d7-8492-ff628a686065";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Check on mount
    checkIsMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkIsMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
}

export default function DelloWidget() {
  const widgetRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [location] = useLocation();

  // Check if we're on the how-it-works page
  const isHowItWorksPage = location === '/how-it-works';

  // Only show widget if:
  // - Desktop: show on all pages
  // - Mobile: only show on how-it-works page
  const shouldShowWidget = !isMobile || (isMobile && isHowItWorksPage);

  // Set dimensions based on mobile detection
  const widgetWidth = isMobile ? 80 : 120;
  const widgetHeight = isMobile ? 80 : 100;
  const headerText = isMobile ? "CALL" : "Call Here";
  const allowLanguageSelection = isMobile ? false : true;

  useEffect(() => {
    // Only add the script if it doesn't already exist and widget should be shown
    if (shouldShowWidget && !document.querySelector(`script[src='${DELLO_WIDGET_SRC}']`)) {
      const script = document.createElement("script");
      script.type = "module";
      script.src = DELLO_WIDGET_SRC;
      document.body.appendChild(script);
    }
  }, [shouldShowWidget]);

  // Don't render anything if widget shouldn't be shown
  if (!shouldShowWidget) {
    return null;
  }

  return (
    <div ref={widgetRef}>
      <dello-ai-widget 
        header-text={headerText}
        allowLanguageSelection={allowLanguageSelection}
        width={widgetWidth} 
        height={widgetHeight} 
        agent-id={AGENT_ID}
      ></dello-ai-widget>
    </div>
  );
} 